import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, OnInit, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { FaqComponent } from '../../../shared/faq/faq.component';
import { FaqItem } from '../../../models/faq.model';
import { ExploreMoreIndustriesComponent } from '../explore-more-industries/explore-more-industries.component';
import { trigger, transition, query, style, stagger, animate } from '@angular/animations';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsComponent } from '../../../shared/contact-us/contact-us.component';

interface CaseStudy {
  image: string;
  title: string;
  description: string;
}
@Component({
  selector: 'app-e-commerce',
  imports: [CommonModule, RouterModule, FaqComponent, ExploreMoreIndustriesComponent],
  standalone: true,
  templateUrl: './e-commerce.component.html',
  styleUrl: './e-commerce.component.scss',
  animations: [
    trigger('fadeInUpStagger', [
      transition('* => visible', [
        query('.animate-item', [
          style({ opacity: 0, transform: 'translateY(30px)' }),
          stagger('200ms', [
            animate('800ms cubic-bezier(0.35, 0, 0.25, 1)', style({ opacity: 1, transform: 'translateY(0)' }))
          ])
        ], { optional: true })
      ])
    ])
  ]
})
export class ECommerceComponent implements AfterViewInit, OnInit, OnDestroy {
  private jsonLdScript: any;
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('impactSection') impactSection!: ElementRef;
  impactSectionVisible = false;
  private observer: IntersectionObserver | null = null;

  caseStudies: CaseStudy[] = [
    {
      image: '../../../../assets/industries/ecommerce/ecommerce1.png',
      title: 'Scalable SaaS Platform',
      description: 'A Saas product, developed by Kalpita Technologies, empowers e-commerce business owners to launch fully customized online stores, bypassing marketplace commissions of around 20% per sale. This end-to-end SaaS solution handles product listings, traffic generation, and secure sales while delivering cost efficiency, seamless scalability, high reliability, and robust security through cloud infrastructure. Businesses gain complete control over operations without hardware maintenance or pricing compromises.'
    },
    {
      image: '../../../../assets/industries/ecommerce/ecommerce2.png',
      title: 'Online Retail E-commerce Platform',
      description: 'We developed a mobile ecommerce platform for a Chilean retail client, featuring three managed access roles, Admin, Seller, and Customer, alongside integrated payment gateways like F-Pay for seamless transactions. The app addresses multi-country operations across Chile, Peru, Colombia, and Argentina by enabling in-app location switching for Spanish-language, region-specific products and offers while maintaining a consistent interface. Firebase Crashlytics ensures real-time crash monitoring to prioritize user-impacting issues.'
    }
    // {
    //   image: '../../../../assets/industries/healthcare/case-study-healthcare.png',
    //   title: 'Unified Customer Engagement Portal',
    //   description: 'Kalpita Technologies partnered with a top-tier insurance provider to modernize their customer engagement ecosystem through a unified digital portal. The platform consolidated multiple service lines into one secure, SOC2 and GDPR compliant environment, enabling seamless client–advisor collaboration, consistent brand communication, and actionable business insights via integrated Power BI dashboards. This transformation enhanced customer retention, improved decision-making, and reinforced the client’s brand identity across all insurance verticals.'
    // }
  ];
  private forcePlayHeroVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;           // required for autoplay
    video.playsInline = true;

    const play = () => {
      video.play().catch(() => {
        // retry if browser blocks
        setTimeout(() => video.play().catch(() => { }), 300);
      });
    };

    // Ensure it runs after Angular rendering
    setTimeout(play, 100);
  }
  EcommerceFaqs: FaqItem[] = [
    {
      question: 'What makes custom e-commerce software better than off-the-shelf solutions?',
      answer: `Custom e-commerce platforms offer unlimited flexibility to match your unique 
      business processes, handle complex B2B or marketplace models, scale without platform fees, 
      and integrate deeply with your existing tech stack. While platforms work for standard retail, 
      custom solutions deliver competitive advantages through proprietary features, superior performance,
      and ownership of your data and customer relationships.`
    },
    {
      question: 'Do you provide B2B e-commerce platform development?',
      answer: `Yes, we specialize in building robust B2B e-commerce systems with custom pricing engines, 
      role-based access, advanced procurement workflows, bulk ordering capabilities, and secure 
      integrations with procurement and accounting systems. Our B2B platform development supports 
      both self-service portals and sales-assisted ordering.`
    },
    {
      question: 'How long does it take to build a custom e-commerce platform?',
      answer: `Timelines vary based on features, integrations, and complexity, but our AI-accelerated 
      approach typically delivers production-ready solutions in 8–20 weeks - significantly 
      faster than traditional development.`
    },
    {
      question: 'Can you integrate payment gateways, CRMs, and ERP systems?',
      answer: `Absolutely. We build API-driven e-commerce platforms that integrate smoothly with CRMs, 
      ERPs, logistics, payment gateways, and marketing tools.`
    },
    {
      question: 'Do you offer ongoing support and maintenance?',
      answer: `Yes, we provide dedicated post-launch support, upgrades, and performance optimization.`
    },
    {
      question: 'Can you migrate to our existing e-commerce site without disrupting sales?',
      answer: `Yes, we follow a phased migration approach with parallel testing, data validation, 
      and SEO preservation. We typically schedule the final cutover during low-traffic windows 
      and maintain the old system as a backup. Most migrations are completed with zero downtime 
      and minimal impact on operations.`
    }
  ];

  currentSlide = 0;
  isDragging = false;
  startPosition = 0;
  currentPosition = 0;
  transformValue = 0;
  private autoSlideInterval: any;
  private readonly SLIDE_DURATION = 5000;

  constructor(private router: Router, private modalService: NgbModal,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
    this.stopAutoSlide();
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  ngAfterViewInit(): void {
    this.forcePlayHeroVideo();
    // 4. Set up Intersection Observer
    const options = {
      root: null,
      threshold: 0.2,
      rootMargin: '0px'
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.impactSectionVisible = true;
          this.observer?.disconnect();
        }
      });
    }, options);

    if (this.impactSection) {
      this.observer.observe(this.impactSection.nativeElement);
    }

    // 5. Initialize 3D Tilt Effect
    this.setupTiltEffect();
  }

  setupTiltEffect(): void {
    const cards = document.querySelectorAll('.solution-image-card, .impact-image-wrapper, .case-study-image-wrapper');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const xc = rect.width / 2;
        const yc = rect.height / 2;
        const maxRotate = 10;

        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate;

        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out';
      };

      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  scheduleDemo(): void {
    this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'contact-modal'
    });
  }

  startAutoSlide(): void {
    this.stopAutoSlide(); // Ensure no duplicate intervals

    // MODIFIED: Only start auto-slide if there is more than 1 slide
    if (this.caseStudies.length > 1) {
      this.autoSlideInterval = setInterval(() => {
        this.nextSlide();
      }, this.SLIDE_DURATION);
    }
  }

  stopAutoSlide(): void {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
  }

  nextSlide(): void {
    if (this.currentSlide < this.caseStudies.length - 1) {
      this.currentSlide++;
    } else {
      this.currentSlide = 0;
    }
  }

  goToSlide(index: number): void {
    if (this.caseStudies.length <= 1) return;
    this.currentSlide = index;
    this.stopAutoSlide();
    this.startAutoSlide();
  }

  onMouseEnter(): void {
    this.stopAutoSlide();
  }

  onMouseLeave(): void {
    this.startAutoSlide();
  }

  onMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    this.startPosition = event.clientX;
    this.currentPosition = this.startPosition;
    clearInterval(this.autoSlideInterval);
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    this.currentPosition = event.clientX;
    this.transformValue = -this.currentSlide * 100 + ((this.currentPosition - this.startPosition) / window.innerWidth * 100);
  }

  onTouchStart(event: TouchEvent): void {
    this.isDragging = true;
    this.startPosition = event.touches[0].clientX;
    this.currentPosition = this.startPosition;
    clearInterval(this.autoSlideInterval);
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.isDragging) return;
    this.currentPosition = event.touches[0].clientX;
    this.transformValue = -this.currentSlide * 100 + ((this.currentPosition - this.startPosition) / window.innerWidth * 100);
  }

  redirectToAbout(): void {
    this.router.navigate(['/about-us']);
  }

  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.startAutoSlide();
  }
  injectSchemaMarkup(): void {
    const ecommerceSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/industries/ecommerce#webpage",
          "url": "https://www.kalpitatechnologies.com/industries/ecommerce",
          "name": "E-commerce Website Development | Custom Retail & B2B Solutions | Kalpita Technologies",
          "description": "End-to-end e-commerce development services. Build scalable, high-converting online stores with headless commerce, PWA, AI personalization, and payment integration.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/industries/ecommerce#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com" },
            { "@type": "ListItem", "position": 2, "name": "Industries", "item": "https://www.kalpitatechnologies.com/industries" },
            { "@type": "ListItem", "position": 3, "name": "E-commerce", "item": "https://www.kalpitatechnologies.com/industries/ecommerce" }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://www.kalpitatechnologies.com/industries/ecommerce#service",
          "name": "E-commerce Website Development Solutions",
          "serviceType": "E-commerce Platform Development",
          "provider": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "description": "End-to-end e-commerce development services delivering custom platforms for retail, B2B, and marketplace models..."
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/industries/ecommerce#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What makes custom e-commerce software better than off-the-shelf solutions?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Custom e-commerce platforms offer unlimited flexibility to match your unique business processes, handle complex B2B models, and scale without platform fees."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(ecommerceSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  onCardClick(index: number): void {
    console.log('Clicked card', index);
  }

  getStarArray(rating: number): boolean[] {
    return Array(5).fill(false).map((_, index) => index < rating);
  }

  testimonials(): void {
    this.router.navigate(['/'], { fragment: 'testimonials' });
  }
}
